import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/Combobox'
import { type ControlSize } from '@/components/ui/control'
import { useSession } from '@/features/auth/api/useSession'
import {
  useClients,
  useCreateClient,
} from '@/features/directories/api/useDirectory'
import {
  ClientFormSheet,
  type ClientDraft,
} from '@/features/directories/components/DirectoryFormSheets'
import { clientSaveErrorMessage } from '@/features/directories/directoryText'
import { can, CAP } from '@/lib/can'
import { describeProblem } from '@/lib/problems'
import type { Client } from '@/types/domain'

/* The client a quote or a case is for — picked from the directory (FR-39),
 * with a way to add one without leaving the form (QW-12, finding Q-1). Used by
 * QuoteFormSheet and CaseFormSheet.
 *
 * ── Why the add-a-client button is inside the list ──
 *
 * A Sales user's client scope is `own`: the list arrives already narrowed by
 * the server (AD-5) to the clients they hold. On a new salesperson's first day
 * that is NONE, and the quote form had no way to create one — so the first
 * quote they tried to open was a dead end until somebody else registered a
 * client for them. The button is the way out, and it is CarrierSelect's
 * escape hatch on the same terms: pinned under the list rather than a row in
 * it, so a filter that matches nothing does not hide it exactly when it is
 * wanted; shown only to a role that may create a client (absent, not disabled
 * — the server would refuse the create regardless).
 *
 * The empty list says WHY it is empty. "No matches" over a list the reader
 * has not searched reads as a broken directory; the copy names the scope and
 * the two ways out — ask a manager to reassign a client the company already
 * has, or create a new one.
 *
 * ── What is offered, and what merely stays selected ──
 *
 * The list offers ACTIVE clients. The one already on the record is kept even
 * when it has since been deactivated, and a record whose client is not in the
 * list at all (reassigned to another salesperson since) still shows the name
 * the caller passes — nothing here may reject data that is already saved.
 */

export interface ClientSelectProps {
  /** The selected client's id, or '' for none. */
  value: string
  /** `created` is present only when the client was just created from the
   *  picker: its contacts are then known before any list refetches, so the
   *  caller can preselect one (N-18). */
  onChange: (id: string, created?: Client) => void
  /** The client's name as the record carries it — what the trigger shows when
   *  the id is not in the caller's list (the client was reassigned to another
   *  salesperson, or the list has not loaded yet). */
  name?: string
  id?: string
  disabled?: boolean
  size?: ControlSize
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/* Base UI: none directly — composed from Combobox.tsx, which is the
   `@base-ui/react` combobox primitive wearing this system's tokens. */
export function ClientSelect({
  value,
  onChange,
  name,
  id,
  disabled,
  size = 'md',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: ClientSelectProps) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const clientsQuery = useClients()
  const clients = clientsQuery.data
  const createClient = useCreateClient()

  const canCreate = can(session, CAP.directoryClientCreate)
  const [adding, setAdding] = useState(false)

  /* The list's own open state, held here for CarrierSelect's reason: the add
     button opens a second sheet, and a combobox popup left open sits ABOVE it —
     both portal to <body> and the popup mounts last (wiki
     components/frontend.md). The list closes before the sheet opens. */
  const [listOpen, setListOpen] = useState(false)

  /* The client created from here, remembered until the list refetches it. The
     create invalidates the directory, but the refetch lands a round trip later,
     and until then the new id matches no row — the trigger would show the
     placeholder over a value that is set. */
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null)

  const all = clients ?? []
  const offered = all
    .filter((client) => client.isActive || client.id === value)
    .sort((a, b) => a.name.localeCompare(b.name))

  const rows = offered.map((client) => ({
    id: client.id,
    label: client.name,
    hint: client.isActive
      ? client.number
      : `${client.number} · ${t('common.form.inactive')}`,
    /* Name, registry number and code, so typing CL-0012 (or 0012) or the short
       code finds the client — the number is how a client is named on paper. */
    search: [client.name, client.number, client.code ?? '']
      .join(' ')
      .toLowerCase(),
  }))
  const byID = new Map(rows.map((row) => [row.id, row]))

  const fallbackLabel =
    (created && created.id === value ? created.name : '') || (name ?? '').trim()

  const submit = (values: ClientDraft) =>
    createClient.mutate(values, {
      // Selected on the record the moment it exists: somebody who has just
      // typed a company's name must not have to find it in a list afterwards.
      // This lands a render later than the Save that sent it, so a caller
      // keeping a draft should apply it with an updater, not a spread.
      onSuccess: (client) => {
        setCreated({ id: client.id, name: client.name })
        onChange(client.id, client)
        setAdding(false)
      },
    })

  return (
    <>
      <Combobox
        items={rows.map((row) => row.id)}
        value={value === '' ? null : value}
        onValueChange={(next: string | null) => onChange(next ?? '')}
        itemToStringLabel={(rowID: string) =>
          byID.get(rowID)?.label ?? (rowID === value ? fallbackLabel : '')
        }
        filter={(rowID: string, query: string) => {
          const needle = query.trim().toLowerCase()
          if (needle === '') return true
          return byID.get(rowID)?.search.includes(needle) ?? false
        }}
        disabled={disabled}
        autoHighlight
        open={listOpen}
        onOpenChange={setListOpen}
      >
        <ComboboxTrigger
          id={id}
          triggerSize={size}
          aria-describedby={describedBy}
          aria-invalid={invalid}
        >
          <ComboboxValue>
            {(rowID: string | null) => {
              const label = (rowID ? byID.get(rowID)?.label : '') || (rowID ? fallbackLabel : '')
              if (!label) {
                return (
                  <span className="text-text-faint truncate">
                    {t('common.form.selectPlaceholder')}
                  </span>
                )
              }
              return <span className="text-text-body truncate">{label}</span>
            }}
          </ComboboxValue>
        </ComboboxTrigger>

        <ComboboxContent>
          <ComboboxSearch placeholder={t('directories.clients.search')} />

          {/* Four different answers to "why is nothing listed".

              An empty RESULT is the search's and says so. With no rows at all,
              the row count cannot tell "none" from "not loaded yet" or "the
              read failed" — `data` is undefined in all three — so the query's
              status decides. The scope copy invites a create, and it is only
              true once the list has ANSWERED with nothing: shown while loading
              or after a failure, it would tell a salesperson with forty
              clients that they have none and invite them to register a
              forty-first. Loading and failure are plain lines rather than
              ComboboxEmpty, whose no-results glyph would misstate both. */}
          {rows.length > 0 ? (
            <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
              {t('common.form.noMatches')}
            </ComboboxEmpty>
          ) : clientsQuery.isPending ? (
            <p
              role="status"
              className="text-text-muted px-3 py-6 text-center text-[12.5px] font-light"
            >
              {t('common.loading')}
            </p>
          ) : clientsQuery.isError ? (
            <div className="flex flex-col items-center gap-0.5 px-3 py-6 text-center">
              <span className="text-text-body text-[12.5px] font-normal">
                {t(describeProblem(clientsQuery.error).titleKey)}
              </span>
              <span className="text-text-muted text-[11.5px] font-light">
                {t(describeProblem(clientsQuery.error).detailKey)}
              </span>
            </div>
          ) : (
            <ComboboxEmpty>
              {canCreate
                ? t('directories.clients.pickerEmpty')
                : t('directories.clients.pickerEmptyNoCreate')}
            </ComboboxEmpty>
          )}

          <ComboboxList>
            {(rowID: string) => {
              const row = byID.get(rowID)
              if (!row) return null
              return (
                <ComboboxItem key={rowID} value={rowID}>
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {/* The registry number, one step down and muted — the
                      secondary reading of the same row. */}
                  <span className="text-text-muted shrink-0 font-mono text-[12px]">
                    {row.hint}
                  </span>
                </ComboboxItem>
              )
            }}
          </ComboboxList>

          {canCreate ? (
            <div className="border-border-subtle -mx-1 -mb-1 mt-1 border-t px-1 pt-1">
              <button
                type="button"
                onClick={() => {
                  // The last attempt's refusal belongs to the last attempt.
                  createClient.reset()
                  setListOpen(false)
                  setAdding(true)
                }}
                className="text-brand hover:bg-surface-hover flex w-full cursor-pointer items-center gap-1.5 rounded-[5px] px-2 py-1.5 text-left text-[12.5px]"
              >
                <Plus size={14} aria-hidden />
                {t('directories.clients.new')}
              </button>
            </div>
          ) : null}
        </ComboboxContent>
      </Combobox>

      {/* The directory's own editor, over the caller's form rather than instead
          of it. FormSheet gives each sheet its own form id and stops its submit
          from propagating, and Sheet keeps an Escape inside this one from
          closing the one beneath — so the quote or case draft survives either
          way out. */}
      <ClientFormSheet
        open={adding}
        onClose={() => setAdding(false)}
        pending={createClient.isPending}
        error={
          createClient.error
            ? clientSaveErrorMessage(createClient.error, t)
            : undefined
        }
        onSubmit={submit}
      />
    </>
  )
}
